import { FunctionComponent, useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from 'src/shared';
import { isAdmin } from 'src/redux/auth/service';
import { getTask, getTaskActivities } from 'src/api/tasks';
import { RoutesEnum } from 'src/types/navigation';
import {
    TaskDetail as TaskDetailType,
    TaskActivity,
    getTaskStatusLabel,
    getTaskTypeLabel,
    getTaskStatusColors
} from 'src/types/task';
import { QuestActivityType, QuestStatus } from 'src/enums/quests';
import { ReviewStatus, WorkType as WorkTypeEnum } from 'src/enums/work';
import { ReactComponent as GeneralActivityIcon } from 'src/assets/icons/general_activity.svg';
import { ReactComponent as DescriptionChangeIcon } from 'src/assets/icons/description_change.svg';
import { IAttachment } from 'src/types/attachments';
import { Rating } from 'src/types/rating';
import ActivityUpdate from 'src/shared/ActivityUpdate';
import { MarkdownEditor } from 'src/shared/MarkdownEditor';
import {
    RootBox,
    PageTitle,
    InfoCard,
    InfoRow,
    InfoRowTopAligned,
    InfoLabel,
    InfoValue,
    InfoLink,
    PrLinkList,
    PrLink,
    StatusBadge,
    HistoryTitle,
    HistoryContainer,
    ActivityRow,
    ActivityColumn,
    IconColumn,
    IconBox,
    VerticalLine,
    DateContainer,
    DatePart,
    TimePart,
    EmptyState,
    TextSkeleton,
    CardSkeleton,
    DescriptionMarkdownContainer,
    LlmInfoButton
} from './styled';

type Params = {
    id: string;
};

const useExpandedDescriptions = () => {
    const [expandedDescriptions, setExpandedDescriptions] = useState<{
        [key: number]: boolean;
    }>({});

    const handleDescriptionToggle = useCallback((activityId: number) => {
        setExpandedDescriptions((prev) => ({
            ...prev,
            [activityId]: !prev[activityId]
        }));
    }, []);

    return { expandedDescriptions, handleDescriptionToggle };
};

const handleIcon = (activityType: QuestActivityType) => {
    switch (activityType) {
        case QuestActivityType.DESCRIPTION_UPDATED:
            return <DescriptionChangeIcon />;
        default:
            return <GeneralActivityIcon />;
    }
};

const TaskDetail: FunctionComponent = () => {
    const { id } = useParams<Params>();
    const navigate = useNavigate();
    const [task, setTask] = useState<TaskDetailType | null>(null);
    const [activities, setActivities] = useState<TaskActivity[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { expandedDescriptions, handleDescriptionToggle } =
        useExpandedDescriptions();

    useEffect(() => {
        if (!isAdmin()) {
            navigate('/');
            return;
        }
        if (!id) return;

        setIsLoading(true);
        Promise.all([
            getTask(id)
                .then((res) => setTask(res.data))
                .catch(() => setTask(null)),
            getTaskActivities(id)
                .then((res) => setActivities(Array.isArray(res) ? res : []))
                .catch(() => setActivities([]))
        ]).finally(() => setIsLoading(false));
    }, [id, navigate]);

    return (
        <Navbar>
            <RootBox>
                {isLoading ? (
                    <>
                        <TextSkeleton variant="text" width={300} height={40} />
                        <CardSkeleton variant="rectangular" height={200} />
                    </>
                ) : !task ? (
                    <EmptyState>Task not found</EmptyState>
                ) : (
                    <>
                        <PageTitle>{task.title || 'Untitled Task'}</PageTitle>

                        <InfoCard>
                            <InfoRow>
                                <InfoLabel>Task ID</InfoLabel>
                                <InfoValue>{task.id}</InfoValue>
                            </InfoRow>
                            {task.created && (
                                <InfoRow>
                                    <InfoLabel>Created</InfoLabel>
                                    <InfoValue>
                                        {new Date(
                                            task.created
                                        ).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric'
                                        })}{' '}
                                        {new Date(
                                            task.created
                                        ).toLocaleTimeString('en-US', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            hour12: true
                                        })}
                                    </InfoValue>
                                </InfoRow>
                            )}
                            <InfoRow>
                                <InfoLabel>Status</InfoLabel>
                                <StatusBadge
                                    statusColor={
                                        getTaskStatusColors(task.status).bg
                                    }
                                    statusTextColor={
                                        getTaskStatusColors(task.status)
                                            .textColor
                                    }
                                >
                                    {getTaskStatusLabel(task.status)}
                                </StatusBadge>
                            </InfoRow>
                            <InfoRow>
                                <InfoLabel>Type</InfoLabel>
                                <InfoValue>
                                    {getTaskTypeLabel(task.taskType)}
                                </InfoValue>
                            </InfoRow>
                            {task.questId && (
                                <InfoRow>
                                    <InfoLabel>Quest ID</InfoLabel>
                                    <InfoLink
                                        onClick={() =>
                                            navigate(`/quest/${task.questId}`)
                                        }
                                    >
                                        {task.questId}
                                    </InfoLink>
                                </InfoRow>
                            )}
                            {task.iteration != null && (
                                <InfoRow>
                                    <InfoLabel>Iteration</InfoLabel>
                                    <InfoValue>{task.iteration}</InfoValue>
                                </InfoRow>
                            )}
                            {task.description && (
                                <InfoRowTopAligned>
                                    <InfoLabel>Description</InfoLabel>
                                    <DescriptionMarkdownContainer>
                                        <MarkdownEditor
                                            value={task.description}
                                            readOnly
                                        />
                                    </DescriptionMarkdownContainer>
                                </InfoRowTopAligned>
                            )}
                            {task.solutionUrls &&
                                task.solutionUrls.length > 0 && (
                                    <InfoRowTopAligned>
                                        <InfoLabel>Solution PRs</InfoLabel>
                                        <PrLinkList>
                                            {task.solutionUrls.map((url) => (
                                                <PrLink
                                                    key={url}
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {url}
                                                </PrLink>
                                            ))}
                                        </PrLinkList>
                                    </InfoRowTopAligned>
                                )}
                            {isAdmin() && (
                                <>
                                    <InfoRow>
                                        <InfoLabel>Contributor Cost</InfoLabel>
                                        <InfoValue>
                                            $
                                            {Number(
                                                task.contributorCostUsd
                                            ).toFixed(2)}
                                        </InfoValue>
                                    </InfoRow>
                                    <InfoRow>
                                        <InfoLabel>LLM Cost</InfoLabel>
                                        <InfoValue>
                                            $
                                            {Number(task.llmCostUsd).toFixed(2)}
                                        </InfoValue>
                                    </InfoRow>
                                    <InfoRow>
                                        <InfoLabel>Total Cost</InfoLabel>
                                        <InfoValue>
                                            $
                                            {(
                                                Number(
                                                    task.contributorCostUsd
                                                ) + Number(task.llmCostUsd)
                                            ).toFixed(2)}
                                        </InfoValue>
                                    </InfoRow>
                                    <InfoRow>
                                        <InfoLabel>LLM info</InfoLabel>
                                        <LlmInfoButton
                                            variant="outlined"
                                            size="small"
                                            onClick={() =>
                                                navigate(
                                                    RoutesEnum.TASK_LLM_INFO.replace(
                                                        ':id',
                                                        task.id
                                                    )
                                                )
                                            }
                                        >
                                            View sessions
                                        </LlmInfoButton>
                                    </InfoRow>
                                </>
                            )}
                        </InfoCard>

                        <HistoryTitle>History</HistoryTitle>
                        <HistoryContainer>
                            {activities.length === 0 ? (
                                <EmptyState>
                                    No activity history for this task
                                </EmptyState>
                            ) : (
                                activities
                                    .sort(
                                        (a, b) =>
                                            b.created.localeCompare(
                                                a.created
                                            ) || b.id - a.id
                                    )
                                    .map((activity, index) => {
                                        const details = activity.details || {};
                                        const {
                                            text,
                                            status,
                                            statusChange = {},
                                            ratings,
                                            implementationPlanRequired = false,
                                            workType,
                                            taskTitle,
                                            taskId: detailsTaskId,
                                            tasksToCreate,
                                            tasksDependencies,
                                            netWorkTime,
                                            grossWorkTime,
                                            rejectionReason,
                                            solveByReviewer,
                                            seniorReviewerName,
                                            blockerDescription,
                                            quickQuest,
                                            attachments,
                                            reviewStatus,
                                            priority = {}
                                        } = details as {
                                            text?: string;
                                            status?: QuestStatus;
                                            statusChange?: {
                                                from?: QuestStatus;
                                                to?: QuestStatus;
                                            };
                                            ratings?: Rating[];
                                            implementationPlanRequired?: boolean;
                                            workType?: WorkTypeEnum;
                                            taskTitle?: string;
                                            taskId?: string;
                                            tasksToCreate?: string[];
                                            tasksDependencies?: {
                                                [taskTitle: string]:
                                                    | string[]
                                                    | number[];
                                            };
                                            netWorkTime?: string;
                                            grossWorkTime?: string;
                                            rejectionReason?: string;
                                            solveByReviewer?: boolean;
                                            seniorReviewerName?: string;
                                            blockerDescription?: string;
                                            quickQuest?: boolean;
                                            attachments?: IAttachment[];
                                            reviewStatus?: ReviewStatus;
                                            priority?: {
                                                from?: number;
                                                to?: number;
                                            };
                                        };

                                        const { from, to } = statusChange || {};

                                        const date = new Date(activity.created);
                                        const datePart =
                                            date.toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric'
                                            });
                                        const timePart =
                                            date.toLocaleTimeString('en-US', {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: true
                                            });

                                        return (
                                            <ActivityRow key={activity.id}>
                                                <ActivityColumn>
                                                    <DateContainer>
                                                        <DatePart>
                                                            {datePart}
                                                        </DatePart>
                                                        <TimePart>
                                                            {timePart}
                                                        </TimePart>
                                                    </DateContainer>
                                                </ActivityColumn>
                                                <IconColumn>
                                                    <IconBox>
                                                        {handleIcon(
                                                            activity.activityType
                                                        )}
                                                        {index !==
                                                            activities.length -
                                                                1 && (
                                                            <VerticalLine />
                                                        )}
                                                    </IconBox>
                                                </IconColumn>
                                                <ActivityColumn>
                                                    <ActivityUpdate
                                                        activity_type={
                                                            activity.activityType
                                                        }
                                                        user={activity.user}
                                                        activityId={activity.id}
                                                        text={text}
                                                        implementationPlanRequired={
                                                            implementationPlanRequired
                                                        }
                                                        quickQuest={quickQuest}
                                                        status={status}
                                                        from={from}
                                                        to={to}
                                                        ratings={ratings}
                                                        workType={workType}
                                                        taskTitle={taskTitle}
                                                        taskId={detailsTaskId}
                                                        tasksToCreate={
                                                            tasksToCreate
                                                        }
                                                        tasksDependencies={
                                                            tasksDependencies
                                                        }
                                                        netWorkTime={
                                                            netWorkTime
                                                        }
                                                        grossWorkTime={
                                                            grossWorkTime
                                                        }
                                                        rejectionReason={
                                                            rejectionReason
                                                        }
                                                        solveByReviewer={
                                                            solveByReviewer
                                                        }
                                                        seniorReviewerName={
                                                            seniorReviewerName
                                                        }
                                                        expandedDescriptions={
                                                            expandedDescriptions
                                                        }
                                                        blockerDescription={
                                                            blockerDescription
                                                        }
                                                        attachments={
                                                            attachments
                                                        }
                                                        reviewStatus={
                                                            reviewStatus
                                                        }
                                                        onToggle={
                                                            handleDescriptionToggle
                                                        }
                                                        priorityFrom={
                                                            priority?.from
                                                        }
                                                        priorityTo={
                                                            priority?.to
                                                        }
                                                    />
                                                </ActivityColumn>
                                            </ActivityRow>
                                        );
                                    })
                            )}
                        </HistoryContainer>
                    </>
                )}
            </RootBox>
        </Navbar>
    );
};

export default TaskDetail;
